import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { formatDMY, todayISO } from "@/lib/dateFormat";
import { useConfirm } from "@/hooks/useConfirm";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, Pencil, Calendar as CalendarIcon, CalendarClock, ExternalLink, Eye, Upload, X, Loader2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import HomeworkWall from "@/components/HomeworkWall";
import { ResourcePicker } from "@/components/ResourcePicker";

const emptyForm = () => ({ date: todayISO(), title: "", description: "", due_date: "", link: "" });

const ALLOWED_MIME = [
  "image/jpeg", "image/png", "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "audio/mpeg", "audio/mp3",
];
const MAX_BYTES = 10 * 1024 * 1024;

export default function TeacherHomework() {
  const { t, lang } = useI18n();
  const confirm = useConfirm();
  const [params, setParams] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState(params.get("group") || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [stagedResources, setStagedResources] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [wallHw, setWallHw] = useState(null);
  const [wallInitialStudent, setWallInitialStudent] = useState(null);
  const [stagedFiles, setStagedFiles] = useState([]); // File[] to upload as material on save
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get("/groups").then((r) => {
      setGroups(r.data);
      if (!groupId && r.data.length) setGroupId(r.data[0].id);
    });
  }, []);

  const reload = async (gid) => {
    if (!gid) return;
    const r = await api.get("/homework", { params: { group_id: gid } });
    setItems(r.data);
  };

  useEffect(() => {
    if (!groupId) return;
    setParams({ group: groupId });
    reload(groupId);
  }, [groupId]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setStagedFiles([]);
    setStagedResources([]);
    setOpen(true);
  };
  const openEdit = (hw) => {
    setEditingId(hw.id);
    setForm({
      date: hw.date || todayISO(),
      title: hw.title || "",
      description: hw.description || "",
      due_date: hw.due_date || "",
      link: hw.link || "",
    });
    setStagedFiles([]);
    setStagedResources([]);
    setOpen(true);
  };

  const pickFiles = (fileList) => {
    const picked = Array.from(fileList || []);
    const accepted = [];
    for (const f of picked) {
      if (f.size > MAX_BYTES) { toast.error(`${f.name}: máx 10 MB`); continue; }
      if (!ALLOWED_MIME.includes(f.type)) { toast.error(`${f.name}: tipo no soportado`); continue; }
      accepted.push(f);
    }
    if (accepted.length) setStagedFiles((s) => [...s, ...accepted].slice(0, 5));
  };
  const removeStaged = (idx) => setStagedFiles((s) => s.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!form.title) { toast.error(t("error_generic")); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        due_date: form.due_date || null,
        link: form.link || null,
      };
      let hwId = editingId;
      if (editingId) {
        await api.put(`/homework/${editingId}`, payload);
      } else {
        const r = await api.post("/homework", { ...payload, group_id: groupId });
        hwId = r.data?.id;
      }
      // Upload staged files as material (if any)
      if (hwId && stagedFiles.length) {
        for (const f of stagedFiles) {
          const fd = new FormData();
          fd.append("file", f);
          await api.post(`/homework/${hwId}/material`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
      }
      if (hwId && stagedResources.length) {
        await api.post(`/homework/${hwId}/resources`, { resource_ids: stagedResources.map((r) => r.id) });
      }
      toast.success(t("saved"));
      setOpen(false);
      setStagedFiles([]);
      setStagedResources([]);
      reload(groupId);
    } catch {
      toast.error(t("error_generic"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, title) => {
    const ok = await confirm({
      title: "¿Borrar tarea?",
      description: `Se eliminará "${title || 'esta tarea'}" con sus comentarios, entregas y archivos adjuntos.\n\nEsta acción no se puede deshacer.`,
      confirmText: "Sí, borrar",
      danger: true,
    });
    if (!ok) return;
    await api.delete(`/homework/${id}`);
    reload(groupId);
  };

  return (
    <div>
      <PageHeader
        title={t("homework")}
        testId="homework-header"
        action={
          <Button data-testid="new-homework-button" onClick={openCreate} disabled={!groupId}>
            <Plus className="h-4 w-4" /> {t("new_homework")}
          </Button>
        }
      />
      <div className="mb-5 max-w-md">
        <Label>{t("group")}</Label>
        <Select value={groupId} onValueChange={setGroupId}>
          <SelectTrigger data-testid="homework-group-select"><SelectValue placeholder={t("select_group")} /></SelectTrigger>
          <SelectContent>
            {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {items.length === 0 && (
          <div className="p-10 text-center text-slate-500">{t("no_data")}</div>
        )}
        {items.map((hw, idx) => {
          const unread = hw.unread_channel_count || 0;
          const submissionsUnread = hw.unread_count || 0;
          const groupName = groups.find((g) => g.id === hw.group_id)?.name || "";
          return (
            <div
              key={hw.id}
              className={`flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors ${idx !== 0 ? "border-t border-slate-100" : ""}`}
              data-testid={`homework-item-${hw.id}`}
            >
              <span className="text-[11px] text-slate-500 whitespace-nowrap hidden sm:flex items-center gap-1">
                <CalendarIcon className="h-3 w-3" /> {hw.date}
              </span>
              {hw.due_date && (
                <span className="text-[11px] text-orange-700 whitespace-nowrap hidden md:flex items-center gap-1 font-medium">
                  <CalendarClock className="h-3 w-3" /> {formatDMY(hw.due_date, lang)}
                </span>
              )}
              <span className="text-[11px] text-slate-500 whitespace-nowrap hidden lg:inline truncate max-w-[160px]">
                {groupName}
              </span>
              <span className="flex-1 text-sm font-medium text-slate-900 truncate">{hw.title}</span>
              <button
                type="button"
                onClick={() => { setWallInitialStudent(null); setWallHw(hw); }}
                className="relative inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-slate-100 text-slate-600 shrink-0"
                title="Ver conversación y adjuntos"
                data-testid={`open-wall-${hw.id}`}
              >
                <Eye className="h-4 w-4" />
                {(unread + submissionsUnread) > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-blue-600 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center leading-none">
                    {(unread + submissionsUnread) > 9 ? "9+" : (unread + submissionsUnread)}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => openEdit(hw)}
                className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-slate-100 text-slate-600 shrink-0"
                title={t("edit")}
                data-testid={`edit-homework-${hw.id}`}
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => remove(hw.id, hw.title)}
                className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-rose-50 text-rose-600 shrink-0"
                title={t("delete")}
                data-testid={`delete-homework-${hw.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? t("edit_homework") : t("new_homework")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("date")}</Label>
                <Input type="date" data-testid="homework-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div>
                <Label>{t("due_date")}</Label>
                <Input type="date" data-testid="homework-due-date-input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>{t("title")}</Label>
              <Input data-testid="homework-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label>{t("description")}</Label>
              <Textarea rows={5} data-testid="homework-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label>{t("link")}</Label>
              <Input
                type="url"
                placeholder="https://..."
                data-testid="homework-link-input"
                value={form.link}
                onChange={(e) => setForm({ ...form, link: e.target.value })}
              />
              <p className="text-[11px] text-slate-500 mt-1">{t("homework_link_hint")}</p>
            </div>

            {/* Study resources from the platform catalog */}
            <div>
              <Label>Recursos de la plataforma</Label>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {stagedResources.map((r) => (
                  <span key={r.id} className="inline-flex items-center gap-1.5 bg-indigo-50 border border-indigo-200 rounded-md px-2 py-1 text-[11px]" data-testid={`staged-resource-${r.id}`}>
                    <span className="text-slate-800 truncate max-w-[170px]" title={r.title}>{r.title}</span>
                    <button type="button" onClick={() => setStagedResources((s) => s.filter((x) => x.id !== r.id))} className="text-slate-400 hover:text-red-600" data-testid={`staged-resource-remove-${r.id}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setPickerOpen(true)} data-testid="open-resource-picker">
                  <BookOpen className="h-3 w-3" /> Elegir de Recursos
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">Se agrupan por categoría y podés marcar varios a la vez.</p>
            </div>

            {/* Materials (files) — attach when creating the homework */}
            <div>
              <Label>Materiales de apoyo</Label>              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); pickFiles(e.dataTransfer.files); }}
                className="mt-1 border-2 border-dashed border-slate-300 hover:border-brand-blue hover:bg-slate-50 rounded-lg p-4 text-center cursor-pointer transition-colors"
                data-testid="homework-material-drop"
              >
                <Upload className="h-5 w-5 mx-auto mb-1 text-slate-400" />
                <p className="text-xs text-slate-600">
                  <span className="font-semibold text-brand-blue">Elige archivos</span> o arrástralos aquí
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  PDF, imágenes, Word, Excel, PPT, audio · máx 10 MB · hasta 5
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ALLOWED_MIME.join(",")}
                  className="hidden"
                  onChange={(e) => { pickFiles(e.target.files); e.target.value = ""; }}
                  data-testid="homework-material-input"
                />
              </div>
              {stagedFiles.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {stagedFiles.map((f, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5">
                      <div className="text-xs text-slate-800 truncate flex-1" title={f.name}>{f.name}</div>
                      <div className="text-[10px] text-slate-400">{(f.size / 1024).toFixed(0)} KB</div>
                      <button
                        type="button"
                        onClick={() => removeStaged(i)}
                        className="text-slate-400 hover:text-rose-600"
                        data-testid={`remove-staged-${i}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {editingId && (
                <p className="text-[10px] text-amber-700 mt-1.5">
                  Estos archivos se añadirán a los materiales existentes.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>{t("cancel")}</Button>
            <Button data-testid="save-homework-button" onClick={submit} disabled={saving}>
              {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ResourcePicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        selected={stagedResources.map((r) => r.id)}
        onConfirm={async (ids) => {
          const r = await api.get("/resources");
          setStagedResources((r.data || []).filter((x) => ids.includes(x.id)));
          setPickerOpen(false);
        }}
      />

      <Dialog open={!!wallHw} onOpenChange={(o) => { if (!o) { setWallHw(null); setWallInitialStudent(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="homework-dialog-title">
              {wallHw?.group_name ? `${wallHw.group_name} — ${wallHw?.title}` : wallHw?.title}
            </DialogTitle>
          </DialogHeader>
          {wallHw && <HomeworkWall homework={wallHw} initialStudentId={wallInitialStudent} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
